import {
  collection, getDocs, doc, updateDoc, Timestamp, writeBatch
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Domain } from '../../types';

const DOMAINS_COLLECTION = 'domains';

const toDateOrNull = (ts: any): Date | undefined => {
  if (!ts) return undefined;
  if (ts instanceof Timestamp) return ts.toDate();
  if (ts instanceof Date) return ts;
  return new Date(ts);
};

const docToDomain = (id: string, data: any): Domain => ({
  id,
  domain: data.domain || '',
  domainId: data.domainId || '',
  avdeling: data.avdeling || '',
  innehaverFirmanavn: data.innehaverFirmanavn || '',
  innehaverOrgnr: data.innehaverOrgnr || '',
  innehaverFornavn: data.innehaverFornavn || '',
  innehaverEtternavn: data.innehaverEtternavn || '',
  innehaverPostadresse: data.innehaverPostadresse || '',
  innehaverPostnummer: data.innehaverPostnummer || '',
  innehaverSted: data.innehaverSted || '',
  innehaverLand: data.innehaverLand || '',
  innehaverTelefon: data.innehaverTelefon || '',
  innehaverEpost: data.innehaverEpost || '',
  adminFornavn: data.adminFornavn || '',
  adminEtternavn: data.adminEtternavn || '',
  adminOrganisasjon: data.adminOrganisasjon || '',
  adminEpost: data.adminEpost || '',
  adminTelefon: data.adminTelefon || '',
  betalingModus: data.betalingModus || '',
  betalingType: data.betalingType || '',
  betalingFirmanavn: data.betalingFirmanavn || '',
  betalingOrgnr: data.betalingOrgnr || '',
  betalingFornavn: data.betalingFornavn || '',
  betalingEtternavn: data.betalingEtternavn || '',
  betalingPostadresse: data.betalingPostadresse || '',
  betalingPostnummer: data.betalingPostnummer || '',
  betalingSted: data.betalingSted || '',
  betalingLand: data.betalingLand || '',
  betalingTelefon: data.betalingTelefon || '',
  betalingEpost: data.betalingEpost || '',
  fakturaValuta: data.fakturaValuta || '',
  fakturaType: data.fakturaType || '',
  betalingsreferanse: data.betalingsreferanse || '',
  transferStatus: data.transferStatus || 'keep',
  internalNotes: data.internalNotes || '',
  updatedAt: toDateOrNull(data.updatedAt),
  updatedBy: data.updatedBy || '',
});

export const domainsService = {
  async getDomains(): Promise<Domain[]> {
    const snapshot = await getDocs(collection(db, DOMAINS_COLLECTION));
    return snapshot.docs.map(d => docToDomain(d.id, d.data()));
  },

  async updateDomain(id: string, updates: Partial<Domain>, email: string): Promise<void> {
    const ref = doc(db, DOMAINS_COLLECTION, id);
    const data: any = { ...updates, updatedAt: Timestamp.now(), updatedBy: email };
    delete data.id;
    await updateDoc(ref, data);
  },

  async seedDomains(seedData: Omit<Domain, 'id' | 'updatedAt' | 'updatedBy'>[]): Promise<number> {
    const batchSize = 400;
    let imported = 0;
    for (let i = 0; i < seedData.length; i += batchSize) {
      const batch = writeBatch(db);
      const chunk = seedData.slice(i, i + batchSize);
      for (const d of chunk) {
        const ref = doc(collection(db, DOMAINS_COLLECTION));
        batch.set(ref, { ...d });
      }
      await batch.commit();
      imported += chunk.length;
    }
    return imported;
  },

  async syncDomeneshopFields(
    seedData: Omit<Domain, 'id' | 'updatedAt' | 'updatedBy' | 'transferStatus' | 'internalNotes'>[],
    email: string
  ): Promise<{ updated: number; created: number; unchanged: number }> {
    const existing = await this.getDomains();
    const byDomainId = new Map<string, Domain>();
    existing.forEach(d => { if (d.domainId) byDomainId.set(d.domainId, d); });

    let updated = 0, created = 0, unchanged = 0;
    const batchSize = 400;

    const domeneshopKeys: (keyof typeof seedData[number])[] = [
      'domain', 'domainId', 'avdeling',
      'innehaverFirmanavn', 'innehaverOrgnr', 'innehaverFornavn', 'innehaverEtternavn',
      'innehaverPostadresse', 'innehaverPostnummer', 'innehaverSted', 'innehaverLand',
      'innehaverTelefon', 'innehaverEpost',
      'adminFornavn', 'adminEtternavn', 'adminOrganisasjon', 'adminEpost', 'adminTelefon',
      'betalingModus', 'betalingType',
      'betalingFirmanavn', 'betalingOrgnr', 'betalingFornavn', 'betalingEtternavn',
      'betalingPostadresse', 'betalingPostnummer', 'betalingSted', 'betalingLand',
      'betalingTelefon', 'betalingEpost',
      'fakturaValuta', 'fakturaType', 'betalingsreferanse',
    ];

    const ops: { type: 'create' | 'update'; ref: any; data: any }[] = [];

    for (const incoming of seedData) {
      const match = byDomainId.get(incoming.domainId);
      if (match) {
        const changes: any = {};
        for (const key of domeneshopKeys) {
          const existingVal = (match as any)[key] || '';
          const incomingVal = (incoming as any)[key] || '';
          if (existingVal !== incomingVal) changes[key] = incomingVal;
        }
        if (Object.keys(changes).length > 0) {
          changes.updatedAt = Timestamp.now();
          changes.updatedBy = email;
          ops.push({ type: 'update', ref: doc(db, DOMAINS_COLLECTION, match.id), data: changes });
          updated++;
        } else {
          unchanged++;
        }
      } else {
        const ref = doc(collection(db, DOMAINS_COLLECTION));
        ops.push({
          type: 'create',
          ref,
          data: { ...incoming, transferStatus: 'keep', internalNotes: '', updatedAt: Timestamp.now(), updatedBy: email },
        });
        created++;
      }
    }

    for (let i = 0; i < ops.length; i += batchSize) {
      const batch = writeBatch(db);
      const chunk = ops.slice(i, i + batchSize);
      for (const op of chunk) {
        if (op.type === 'create') batch.set(op.ref, op.data);
        else batch.update(op.ref, op.data);
      }
      await batch.commit();
    }

    return { updated, created, unchanged };
  },
};
